import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, ChevronDown, ChevronUp, CreditCard } from 'lucide-react';
import axios from 'axios';
import { numberToWordsIndian } from '../utils/numberToWords';
import { calculateGSTFromInclusive, calculateInvoiceTotals } from '../utils/gstCalculations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GST_RATES = [0, 5, 12, 18, 28];
const UNITS = ['PCS', 'PAC', 'BOX', 'KG', 'LTR', 'MTR'];

function InvoiceForm({ onSuccess }) {
  const [invoiceType, setInvoiceType] = useState('GST');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [placeOfSupply, setPlaceOfSupply] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerContact, setCustomerContact] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerGSTIN, setCustomerGSTIN] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // EMI Details
  const [showEMI, setShowEMI] = useState(false);
  const [emiDetails, setEmiDetails] = useState({
    financer_name: '',
    finance_scheme: '',
    down_payment: '',
    emi_months: '',
    emi_amount: '',
    first_month_emi_amount: '',
    first_emi_start_date: '',
    emi_account_details: '',
    product_insurance_details: '',
    price: ''
  });

  // Custom Fields
  const [customFields, setCustomFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});

  useEffect(() => {
    fetchNextInvoiceNumber();
    fetchCustomFields();
    addNewItem();
  }, []);

  const fetchNextInvoiceNumber = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/invoices/next-number`);
      setInvoiceNumber(response.data.next_number);
    } catch (error) {
      setInvoiceNumber('INV-1');
    }
  };

  const fetchCustomFields = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/custom-fields`);
      setCustomFields(response.data);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    }
  };

  const addNewItem = () => {
    const newItem = {
      id: Date.now().toString(),
      item_name: '',
      description: '',
      unit: 'PCS',
      quantity: 1,
      sale_price: 0,
      mrp: 0,
      hsn_sac_code: '',
      gst_percentage: 18,
      serial_number: '',
      imei1: '',
      imei2: '',
      base_price: 0,
      cgst_amount: 0,
      sgst_amount: 0,
      total_amount: 0,
    };
    setItems([...items, newItem]);
  };

  const removeItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter((item) => item.id !== id));
    }
  };

  const updateItem = (id, field, value) => {
    setItems(
      items.map((item) => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'sale_price' || field === 'quantity' || field === 'gst_percentage') {
            const calculations = calculateGSTFromInclusive(
              updatedItem.sale_price,
              updatedItem.quantity,
              invoiceType === 'GST' ? updatedItem.gst_percentage : 0
            );
            return { ...updatedItem, ...calculations };
          }
          return updatedItem;
        }
        return item;
      })
    );
  };

  const updateEMI = (field, value) => {
    setEmiDetails({ ...emiDetails, [field]: value });
  };

  const totals = calculateInvoiceTotals(items);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const invoiceData = {
        invoice_number: invoiceNumber,
        invoice_type: invoiceType,
        invoice_date: invoiceDate,
        place_of_supply: placeOfSupply,
        customer_name: customerName,
        customer_contact: customerContact,
        customer_address: customerAddress,
        customer_gstin: invoiceType === 'GST' ? customerGSTIN || null : null,
        subtotal: totals.subtotal,
        total_gst: totals.total_gst,
        grand_total: totals.grand_total,
        amount_in_words: numberToWordsIndian(totals.grand_total),
        items: items.map(item => ({
          item_name: item.item_name,
          description: item.description || '',
          unit: item.unit,
          quantity: item.quantity,
          sale_price: item.sale_price,
          mrp: item.mrp || 0,
          hsn_sac_code: item.hsn_sac_code || '',
          gst_percentage: invoiceType === 'GST' ? item.gst_percentage : 0,
          serial_number: item.serial_number || null,
          imei1: item.imei1 || null,
          imei2: item.imei2 || null,
          base_price: item.base_price,
          cgst_amount: item.cgst_amount,
          sgst_amount: item.sgst_amount,
          total_amount: item.total_amount,
        })),
        emi_details: showEMI && emiDetails.financer_name ? {
          financer_name: emiDetails.financer_name || null,
          finance_scheme: emiDetails.finance_scheme || null,
          down_payment: emiDetails.down_payment ? parseFloat(emiDetails.down_payment) : null,
          emi_months: emiDetails.emi_months ? parseInt(emiDetails.emi_months) : null,
          emi_amount: emiDetails.emi_amount ? parseFloat(emiDetails.emi_amount) : null,
          first_month_emi_amount: emiDetails.first_month_emi_amount ? parseFloat(emiDetails.first_month_emi_amount) : null,
          first_emi_start_date: emiDetails.first_emi_start_date || null,
          emi_account_details: emiDetails.emi_account_details || null,
          product_insurance_details: emiDetails.product_insurance_details || null,
          price: emiDetails.price ? parseFloat(emiDetails.price) : null
        } : null,
        custom_fields: Object.keys(customFieldValues).length > 0 ? customFieldValues : null
      };

      await axios.post(`${API_URL}/api/invoices`, invoiceData);
      setSuccess('Invoice created successfully!');
      
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 1500);
    } catch (error) {
      console.error('Error creating invoice:', error);
      setError(error.response?.data?.detail || 'Error creating invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="invoice-form" className="space-y-6 animate-fade-in max-w-5xl mx-auto">
      {error && (
        <div data-testid="form-error" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      
      {success && (
        <div data-testid="form-success" className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Invoice Information */}
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-6">
        <h2 className="text-lg font-semibold text-zinc-900 mb-4">Invoice Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Invoice Type</label>
            <select
              data-testid="invoice-type-select"
              value={invoiceType}
              onChange={(e) => setInvoiceType(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
              required
            >
              <option value="GST">GST</option>
              <option value="Non-GST">Non-GST</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Invoice Number</label>
            <input
            data-testid="invoice-number-input"
            type="text"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
          />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Date</label>
            <input
              data-testid="invoice-date-input"
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Place of Supply</label>
            <input
              data-testid="place-of-supply-input"
              type="text"
              value={placeOfSupply}
              onChange={(e) => setPlaceOfSupply(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
              placeholder="e.g., 19-West Bengal"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Customer Name</label>
            <input
              data-testid="customer-name-input"
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
              placeholder="Enter customer name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Contact Number</label>
            <input
              data-testid="customer-contact-input"
              type="tel"
              value={customerContact}
              onChange={(e) => setCustomerContact(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
              placeholder="Enter contact number"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-zinc-700 mb-1">Customer Address</label>
            <input
              data-testid="customer-address-input"
              type="text"
              value={customerAddress}
              onChange={(e) => setCustomerAddress(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
              placeholder="Enter customer address"
              required
            />
          </div>

          {invoiceType === 'GST' && (
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Customer GSTIN</label>
              <input
                data-testid="customer-gstin-input"
                type="text"
                value={customerGSTIN}
                onChange={(e) => setCustomerGSTIN(e.target.value)}
                className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
                placeholder="Enter GSTIN (optional)"
              />
            </div>
          )}
        </div>
      </div>

      {/* Product Details */}
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-zinc-900">Product Details</h2>
          <button
            type="button"
            data-testid="add-item-btn"
            onClick={addNewItem}
            className="flex items-center gap-2 px-3 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Add Item
          </button>
        </div>

        <div className="space-y-4">
          {items.map((item, index) => (
            <div key={item.id} data-testid={`product-item-${index}`} className="border border-zinc-200 rounded-lg p-4 bg-zinc-50/50">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-medium text-zinc-900 text-sm">Item #{index + 1}</h3>
                <button
                  type="button"
                  onClick={() => removeItem(item.id)}
                  className="text-red-600 hover:text-red-800 transition-colors p-1"
                  disabled={items.length === 1}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-zinc-600 mb-1">Item Name *</label>
                  <input
                    type="text"
                    value={item.item_name}
                    onChange={(e) => updateItem(item.id, 'item_name', e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">HSN/SAC Code</label>
                  <input
                    type="text"
                    value={item.hsn_sac_code}
                    onChange={(e) => updateItem(item.id, 'hsn_sac_code', e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">Unit</label>
                  <select
                    value={item.unit}
                    onChange={(e) => updateItem(item.id, 'unit', e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                  >
                    {UNITS.map((unit) => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2 md:col-span-4">
                  <label className="block text-xs font-medium text-zinc-600 mb-1">Description</label>
                  <textarea
                    value={item.description}
                    onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                    rows={2}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">Quantity *</label>
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                    min="0.01"
                    step="0.01"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">Unit Price (incl. GST) *</label>
                  <input
                    type="number"
                    value={item.sale_price}
                    onChange={(e) => updateItem(item.id, 'sale_price', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">MRP</label>
                  <input
                    type="number"
                    value={item.mrp}
                    onChange={(e) => updateItem(item.id, 'mrp', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                    min="0"
                    step="0.01"
                  />
                </div>

                {invoiceType === 'GST' && (
                  <div>
                    <label className="block text-xs font-medium text-zinc-600 mb-1">GST %</label>
                    <select
                      value={item.gst_percentage}
                      onChange={(e) => updateItem(item.id, 'gst_percentage', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                    >
                      {GST_RATES.map((rate) => (
                        <option key={rate} value={rate}>{rate}%</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">Serial Number</label>
                  <input
                    type="text"
                    value={item.serial_number}
                    onChange={(e) => updateItem(item.id, 'serial_number', e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">IMEI 1</label>
                  <input
                    type="text"
                    value={item.imei1}
                    onChange={(e) => updateItem(item.id, 'imei1', e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">IMEI 2</label>
                  <input
                    type="text"
                    value={item.imei2}
                    onChange={(e) => updateItem(item.id, 'imei2', e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                  />
                </div>
              </div>

              {invoiceType === 'GST' && (
                <div className="mt-3 pt-3 border-t border-zinc-200 grid grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-zinc-600">Base: </span>
                    <span className="font-medium">₹{item.base_price.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-zinc-600">CGST: </span>
                    <span className="font-medium">₹{item.cgst_amount.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-zinc-600">SGST: </span>
                    <span className="font-medium">₹{item.sgst_amount.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-zinc-600">Total: </span>
                    <span className="font-bold text-zinc-900">₹{item.total_amount.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* EMI / Finance Details */}
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden">
        <button
          type="button"
          data-testid="emi-toggle-btn"
          onClick={() => setShowEMI(!showEMI)}
          className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-zinc-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-emerald-700" />
            <span className="font-semibold text-zinc-900">EMI / Finance Details (Optional)</span>
          </div>
          {showEMI ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>

        {showEMI && (
          <div data-testid="emi-section" className="px-6 pb-6 border-t border-zinc-200 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">Financer Name / Finance Scheme</label>
                <input
                  data-testid="financer-name-input"
                  type="text"
                  value={emiDetails.financer_name}
                  onChange={(e) => updateEMI('financer_name', e.target.value)}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                  placeholder="e.g., Bajaj Finance"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">Finance Scheme</label>
                <input
                  type="text"
                  value={emiDetails.finance_scheme}
                  onChange={(e) => updateEMI('finance_scheme', e.target.value)}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                  placeholder="e.g., No Cost EMI"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">Down Payment (DP)</label>
                <input
                  data-testid="down-payment-input"
                  type="number"
                  value={emiDetails.down_payment}
                  onChange={(e) => updateEMI('down_payment', e.target.value)}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                  placeholder="e.g., 5000"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">EMI Months</label>
                <input
                  data-testid="emi-months-input"
                  type="number"
                  value={emiDetails.emi_months}
                  onChange={(e) => updateEMI('emi_months', e.target.value)}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                  placeholder="e.g., 12"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">EMI Amount</label>
                <input
                  data-testid="emi-amount-input"
                  type="number"
                  value={emiDetails.emi_amount}
                  onChange={(e) => updateEMI('emi_amount', e.target.value)}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                  placeholder="e.g., 2500"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">1st Month EMI Amount</label>
                <input
                  type="number"
                  value={emiDetails.first_month_emi_amount}
                  onChange={(e) => updateEMI('first_month_emi_amount', e.target.value)}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                  placeholder="e.g., 3000"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">1st EMI Starting Date</label>
                <input
                  data-testid="first-emi-date-input"
                  type="date"
                  value={emiDetails.first_emi_start_date}
                  onChange={(e) => updateEMI('first_emi_start_date', e.target.value)}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">EMI Debit Account Details</label>
                <input
                  type="text"
                  value={emiDetails.emi_account_details}
                  onChange={(e) => updateEMI('emi_account_details', e.target.value)}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                  placeholder="Bank account details"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">Product Insurance Details</label>
                <input
                  type="text"
                  value={emiDetails.product_insurance_details}
                  onChange={(e) => updateEMI('product_insurance_details', e.target.value)}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                  placeholder="Insurance policy details"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">Finance Price</label>
                <input
                  type="number"
                  value={emiDetails.price}
                  onChange={(e) => updateEMI('price', e.target.value)}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                  placeholder="Total financed amount"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Custom Fields */}
      {customFields.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-6">
          <h2 className="text-lg font-semibold text-zinc-900 mb-4">Additional Fields</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {customFields.map((field) => (
              <div key={field.field_name}>
                <label className="block text-xs font-medium text-zinc-600 mb-1">
                  {field.field_name} {field.is_required && '*'}
                </label>
                {field.field_type === 'select' ? (
                  <select
                    value={customFieldValues[field.field_name] || ''}
                    onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.field_name]: e.target.value })}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                    required={field.is_required}
                  >
                    <option value="">Select...</option>
                    {(field.options || []).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                    value={customFieldValues[field.field_name] || ''}
                    onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.field_name]: e.target.value })}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-sm"
                    required={field.is_required}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Totals */}
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-6">
        <h2 className="text-lg font-semibold text-zinc-900 mb-4">Totals</h2>
        <div className="space-y-2 max-w-md ml-auto">
          <div className="flex justify-between text-zinc-700">
            <span>Subtotal (Base):</span>
            <span className="font-medium font-[Manrope]">₹{totals.subtotal.toFixed(2)}</span>
          </div>
          {invoiceType === 'GST' && (
            <div className="flex justify-between text-zinc-700">
              <span>Total GST (CGST + SGST):</span>
              <span className="font-medium font-[Manrope]">₹{totals.total_gst.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-lg font-bold text-zinc-900 pt-2 border-t border-zinc-200">
            <span>Grand Total:</span>
            <span className="font-[Manrope]">₹{totals.grand_total.toFixed(2)}</span>
          </div>
          <div className="text-sm text-zinc-600 pt-2 italic">
            {numberToWordsIndian(totals.grand_total)}
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          data-testid="submit-invoice-btn"
          disabled={loading}
          className="flex items-center gap-2 px-6 py-3 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          <Save className="w-5 h-5" />
          {loading ? 'Creating Invoice...' : 'Create Invoice'}
        </button>
      </div>
    </form>
  );
}

export default InvoiceForm;
